// src/components/Admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSessionUnlock } from '../../utils/sessionUnlock';
import StoryEditor from './StoryEditor';
import LinkEditor from './LinkEditor';
import GamesEditor from './GamesEditor';
import BalanceAdmin from './BalanceAdmin';


// ============================================
// HIDDEN GAMES REGISTRY (mirrors GamesArcade.js)
// ============================================
const HIDDEN_GAMES = {
  underbelly: {
    id: 'underbelly',
    component: 'EscapeOphidiaUnderground',
    name: 'ESCAPE OPHIDIA GLOBAL (DEV)',
    description: 'An Ophidia Global deep facility. The molt is 41% complete. Get out.',
    unlockKey: 'underbelly',
    color: '#9b7a4a',
    borderColor: '#3a2e1e',
  },
  opu1: {
    id: 'opu1',
    component: 'OPU1',
    name: 'O P U - 1',
    description: 'The oldest processing unit. Neural interface + AI chat.',
    unlockKey: 'opu1',
    color: '#8b00ff',
    borderColor: '#330055',
  },
  dan_lord: {
    id: 'dan_lord',
    component: 'ConspiracyDanLORD',
    name: 'THE SHED SKIN',
    description: 'Conspiracy Dan LORD game. AI-powered bartender + cyberpunk RPG.',
    unlockKey: 'dan_lord',
    color: '#ff0040',
    borderColor: '#441111',
  },
};

// ============================================
// DEV TOOLS PANEL
// ============================================
function DevToolsPanel() {
  const navigate = useNavigate();
  const { unlock, isUnlocked } = useSessionUnlock();

  // Track unlock states reactively
  const [unlockStates, setUnlockStates] = useState(() => {
    const states = {};
    Object.keys(HIDDEN_GAMES).forEach(key => {
      states[key] = sessionStorage.getItem(`unlock_${key}`) === 'true';
    });
    return states;
  });

  // Listen for unlock events from other components
  useEffect(() => {
    function onUnlock(e) {
      const { key } = e.detail;
      setUnlockStates(prev => ({ ...prev, [key]: true }));
    }
    window.addEventListener('arcade-unlock', onUnlock);
    return () => window.removeEventListener('arcade-unlock', onUnlock);
  }, []);

  // Check for stored hashes
  const [storedHashes, setStoredHashes] = useState(() => {
    const hashes = {};
    Object.keys(HIDDEN_GAMES).forEach(key => {
      const hash = localStorage.getItem(`hash_${key}`);
      if (hash) hashes[key] = hash;
    });
    return hashes;
  });

  function handleUnlock(key) {
    unlock(key);
    if (key === 'opu1') {
      sessionStorage.setItem('opu1_unlocked', 'true');
    }
    if (key === 'dan_lord') {
      sessionStorage.setItem('dan_lord_unlocked', 'true');
    }
    setUnlockStates(prev => ({ ...prev, [key]: true }));
  }

  function handleLock(key) {
    sessionStorage.removeItem(`unlock_${key}`);
    if (key === 'opu1') {
      sessionStorage.removeItem('opu1_unlocked');
    }
     if (key === 'dan_lord') {
      sessionStorage.removeItem('dan_lord_unlocked');
    }
    setUnlockStates(prev => ({ ...prev, [key]: false }));
  }

  function handleLaunch(game) {
    // Make sure it's unlocked first
    if (!unlockStates[game.unlockKey]) {
      handleUnlock(game.unlockKey);
    }
    navigate(`/games/${game.component}`);
  }

  function handleUnlockAll() {
    Object.keys(HIDDEN_GAMES).forEach(key => handleUnlock(key));
  }

  function handleLockAll() {
    Object.keys(HIDDEN_GAMES).forEach(key => handleLock(key));
  }

  function handleClearAllHashes() {
    if (!window.confirm('Clear all stored unlock hashes from localStorage?')) return;

    Object.keys(HIDDEN_GAMES).forEach(key => {
      const hash = localStorage.getItem(`hash_${key}`);
      if (hash) {
        localStorage.removeItem(`hash_${key}`);
        localStorage.removeItem(`hashmap_${hash}`);
      }
    });
    setStoredHashes({});
  }

  function handleCopyHash(hash) {
    navigator.clipboard.writeText(hash).then(() => {
      // Brief visual feedback would be nice but keeping it simple
      alert('Hash copied to clipboard');
    });
  }

  return (
    <div>
      {/* Warning Banner */}
      <div style={{
        padding: '12px 15px',
        marginBottom: '20px',
        border: '1px solid #ffb000',
        background: 'rgba(255, 176, 0, 0.05)',
        color: '#ffb000',
        fontSize: '0.85rem',
        textAlign: 'center',
      }}>
        ⚠ DEV TOOLS — These controls affect your current browser session only.
        <br />
        Unlocks persist until the browser tab/window is closed.
      </div>

      {/* Bulk Actions */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        padding: '12px 15px',
        border: '1px solid #222',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}>
        <div style={{ color: '#555', fontSize: '0.85rem' }}>
          BULK ACTIONS:
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            className="bbs-button"
            onClick={handleUnlockAll}
            style={{
              padding: '5px 15px',
              fontSize: '0.8rem',
              borderColor: '#00ff41',
              color: '#00ff41',
            }}
          >
            🔓 UNLOCK ALL
          </button>
          <button
            className="bbs-button"
            onClick={handleLockAll}
            style={{
              padding: '5px 15px',
              fontSize: '0.8rem',
              borderColor: '#ff0040',
              color: '#ff0040',
            }}
          >
            🔒 LOCK ALL
          </button>
          <button
            className="bbs-button"
            onClick={handleClearAllHashes}
            style={{
              padding: '5px 15px',
              fontSize: '0.8rem',
              borderColor: '#555',
              color: '#555',
            }}
          >
            ✗ CLEAR HASHES
          </button>
          <button
            className="bbs-button"
            onClick={() => navigate('/games')}
            style={{
              padding: '5px 15px',
              fontSize: '0.8rem',
            }}
          >
            → VIEW ARCADE
          </button>
        </div>
      </div>

      {/* Hidden Games List */}
      {Object.entries(HIDDEN_GAMES).map(([key, game]) => {
        const isActive = unlockStates[key];
        const hash = storedHashes[key];

        return (
          <div
            key={key}
            style={{
              border: `1px solid ${isActive ? game.borderColor : '#222'}`,
              background: isActive ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
              padding: '15px',
              marginBottom: '12px',
              transition: 'all 0.3s ease',
            }}
          >
            {/* Game Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '10px',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
              <div>
                <div style={{
                  color: game.color,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  fontFamily: "'VT323', monospace",
                  letterSpacing: '2px',
                }}>
                  {game.name}
                </div>
                <div style={{
                  color: '#555',
                  fontSize: '0.85rem',
                  marginTop: '3px',
                }}>
                  {game.description}
                </div>
              </div>

              {/* Status Badge */}
              <div style={{
                padding: '3px 12px',
                border: `1px solid ${isActive ? '#00ff41' : '#ff0040'}`,
                color: isActive ? '#00ff41' : '#ff0040',
                fontSize: '0.75rem',
                letterSpacing: '1px',
                whiteSpace: 'nowrap',
              }}>
                {isActive ? '● UNLOCKED' : '○ LOCKED'}
              </div>
            </div>

            {/* Game Details */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginBottom: '12px',
              fontSize: '0.8rem',
            }}>
              <div>
                <span style={{ color: '#555' }}>Unlock Key: </span>
                <span style={{ color: '#888' }}>{key}</span>
              </div>
              <div>
                <span style={{ color: '#555' }}>Component: </span>
                <span style={{ color: '#888' }}>{game.component}</span>
              </div>
              <div>
                <span style={{ color: '#555' }}>Route: </span>
                <span style={{ color: '#888' }}>/games/{game.component}</span>
              </div>
              <div>
                <span style={{ color: '#555' }}>Session Key: </span>
                <span style={{ color: '#888' }}>unlock_{key}</span>
              </div>
            </div>

            {/* Stored Hash (if exists) */}
            {hash && (
              <div style={{
                padding: '8px 10px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${game.borderColor}`,
                marginBottom: '12px',
              }}>
                <div style={{
                  color: '#555',
                  fontSize: '0.75rem',
                  marginBottom: '4px',
                }}>
                  STORED UNLOCK HASH:
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <code style={{
                    color: game.color,
                    fontSize: '0.7rem',
                    fontFamily: "'Courier New', monospace",
                    wordBreak: 'break-all',
                    flex: 1,
                    userSelect: 'all',
                  }}>
                    {hash}
                  </code>
                  <button
                    onClick={() => handleCopyHash(hash)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${game.borderColor}`,
                      color: game.color,
                      cursor: 'pointer',
                      padding: '2px 8px',
                      fontFamily: "'VT323', monospace",
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    COPY
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
              {isActive ? (
                <>
                  <button
                    className="bbs-button"
                    onClick={() => handleLaunch(game)}
                    style={{
                      padding: '6px 20px',
                      fontSize: '0.85rem',
                      borderColor: game.color,
                      color: game.color,
                    }}
                  >
                    ▶ LAUNCH GAME
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => handleLock(key)}
                    style={{
                      padding: '6px 15px',
                      fontSize: '0.85rem',
                      borderColor: '#ff0040',
                      color: '#ff0040',
                    }}
                  >
                    🔒 LOCK
                  </button>
                </>
              ) : (
                <button
                  className="bbs-button"
                  onClick={() => handleUnlock(key)}
                  style={{
                    padding: '6px 20px',
                    fontSize: '0.85rem',
                    borderColor: '#00ff41',
                    color: '#00ff41',
                  }}
                >
                  🔓 UNLOCK
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Session Storage Debug */}
      <div style={{
        marginTop: '25px',
        padding: '12px 15px',
        border: '1px solid #1a1a1a',
      }}>
        <div style={{
          color: '#555',
          fontSize: '0.8rem',
          marginBottom: '10px',
          letterSpacing: '1px',
        }}>
          SESSION STORAGE DEBUG:
        </div>
        <div style={{ fontSize: '0.75rem', fontFamily: "'Courier New', monospace" }}>
          {Object.keys(HIDDEN_GAMES).map(key => (
            <div key={key} style={{ marginBottom: '3px' }}>
              <span style={{ color: '#555' }}>unlock_{key}: </span>
              <span style={{
                color: sessionStorage.getItem(`unlock_${key}`) === 'true' ? '#00ff41' : '#ff0040',
              }}>
                {sessionStorage.getItem(`unlock_${key}`) || 'null'}
              </span>
            </div>
          ))}
          <div style={{ marginBottom: '3px' }}>
            <span style={{ color: '#555' }}>opu1_unlocked: </span>
            <span style={{
              color: sessionStorage.getItem('opu1_unlocked') === 'true' ? '#00ff41' : '#ff0040',
            }}>
              {sessionStorage.getItem('opu1_unlocked') || 'null'}
            </span>
          </div>
          <div style={{ marginBottom: '3px' }}>
            <span style={{ color: '#555' }}>dan_lord_unlocked: </span>
            <span style={{
              color: sessionStorage.getItem('dan_lord_unlocked') === 'true' ? '#00ff41' : '#ff0040',
            }}>
              {sessionStorage.getItem('dan_lord_unlocked') || 'null'}
            </span>
          </div>
        </div>

        <div style={{
          color: '#555',
          fontSize: '0.8rem',
          marginTop: '15px',
          marginBottom: '10px',
          letterSpacing: '1px',
        }}>
          LOCAL STORAGE (HASHES):
        </div>
        <div style={{ fontSize: '0.75rem', fontFamily: "'Courier New', monospace" }}>
          {Object.keys(HIDDEN_GAMES).map(key => {
            const hash = localStorage.getItem(`hash_${key}`);
            return (
              <div key={key} style={{ marginBottom: '3px' }}>
                <span style={{ color: '#555' }}>hash_{key}: </span>
                <span style={{
                  color: hash ? '#ffb000' : '#333',
                  wordBreak: 'break-all',
                }}>
                  {hash ? `${hash.substring(0, 20)}...` : 'null'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================
// SUBSCRIBERS PANEL
// ============================================
function SubscribersPanel({ token }) {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/subscribe/admin', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to load subscribers (${res.status})`);
      }

      const data = await res.json();
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[SUBSCRIBERS] Load error:', err.message);
      setError(err.message);
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  async function handleDelete(id, email) {
    if (!window.confirm(`Remove subscriber: ${email}?`)) return;

    setDeletingId(id);
    try {
      const res = await fetch(`/api/subscribe/admin/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setSubscribers(prev => prev.filter(s => s.id !== id));
      } else {
        alert('Failed to remove subscriber');
      }
    } catch (err) {
      console.error('[SUBSCRIBERS] Delete error:', err.message);
      alert('Error removing subscriber');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleExport() {
    try {
      const res = await fetch('/api/subscribe/admin/export', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Export failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[SUBSCRIBERS] Export error:', err.message);
      alert('Failed to export subscribers');
    }
  }

  const activeCount = subscribers.filter(s => s.active !== false).length;
  const inactiveCount = subscribers.length - activeCount;

  if (loading) {
    return (
      <div style={{ color: '#555', padding: '20px', textAlign: 'center' }}>
        Loading subscribers...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: '#ff0040', marginBottom: '15px' }}>
          ✗ {error}
        </div>
        <button className="bbs-button" onClick={fetchSubscribers}>
          RETRY
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Subscriber Stats */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        padding: '12px 15px',
        border: '1px solid #222',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div>
            <span style={{ color: '#555', fontSize: '0.8rem' }}>TOTAL: </span>
            <span style={{ color: '#00ff41', fontSize: '1.1rem' }}>{subscribers.length}</span>
          </div>
          <div>
            <span style={{ color: '#555', fontSize: '0.8rem' }}>ACTIVE: </span>
            <span style={{ color: '#00ff41', fontSize: '1.1rem' }}>{activeCount}</span>
          </div>
          {inactiveCount > 0 && (
            <div>
              <span style={{ color: '#555', fontSize: '0.8rem' }}>INACTIVE: </span>
              <span style={{ color: '#ff0040', fontSize: '1.1rem' }}>{inactiveCount}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="bbs-button"
            onClick={handleExport}
            disabled={subscribers.length === 0}
            style={{ padding: '5px 15px', fontSize: '0.8rem' }}
          >
            ► EXPORT CSV
          </button>
          <button
            className="bbs-button"
            onClick={fetchSubscribers}
            style={{ padding: '5px 15px', fontSize: '0.8rem' }}
          >
            ↻ REFRESH
          </button>
        </div>
      </div>

      {/* Subscriber List */}
      {subscribers.length === 0 ? (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#555',
          border: '1px solid #222',
        }}>
          No subscribers yet. The signal awaits its first listener.
        </div>
      ) : (
        <div style={{ border: '1px solid #222' }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 160px 80px 60px',
            padding: '10px 15px',
            borderBottom: '1px solid #333',
            color: '#ffb000',
            fontSize: '0.8rem',
            fontWeight: 'bold',
          }}>
            <span>EMAIL</span>
            <span>SUBSCRIBED</span>
            <span>SOURCE</span>
            <span style={{ textAlign: 'center' }}>DEL</span>
          </div>

          {/* Subscriber Rows */}
          {subscribers.map((sub, index) => (
            <div
              key={sub.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 160px 80px 60px',
                padding: '10px 15px',
                borderBottom: index < subscribers.length - 1 ? '1px solid #1a1a1a' : 'none',
                alignItems: 'center',
                opacity: sub.active === false ? 0.5 : 1,
              }}
            >
              <span style={{
                color: '#00ff41',
                fontSize: '0.9rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {sub.email}
                {sub.active === false && (
                  <span style={{ color: '#ff0040', fontSize: '0.75rem', marginLeft: '8px' }}>
                    [INACTIVE]
                  </span>
                )}
              </span>

              <span style={{ color: '#555', fontSize: '0.8rem' }}>
                {sub.subscribedAt
                  ? new Date(sub.subscribedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'
                }
              </span>

              <span style={{ color: '#555', fontSize: '0.8rem' }}>
                {sub.source || 'website'}
              </span>

              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => handleDelete(sub.id, sub.email)}
                  disabled={deletingId === sub.id}
                  style={{
                    background: 'transparent',
                    border: '1px solid #ff0040',
                    color: '#ff0040',
                    cursor: deletingId === sub.id ? 'wait' : 'pointer',
                    padding: '2px 8px',
                    fontFamily: "'VT323', 'Courier New', monospace",
                    fontSize: '0.8rem',
                    opacity: deletingId === sub.id ? 0.5 : 1,
                  }}
                >
                  {deletingId === sub.id ? '...' : '✗'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// ADMIN DASHBOARD
// ============================================
export default function AdminDashboard() {
  const { token, isAuthenticated, logout, authFetch } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('stories');
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated && !token) {
      navigate('/admin');
    }
  }, [isAuthenticated, token, navigate]);

  const fetchStories = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setAuthError(false);
    try {
      const res = await authFetch('/api/stories/admin/all');

      if (res.status === 401 || res.status === 403) {
        console.error('[ADMIN] Auth failed — session expired');
        setAuthError(true);
        setStories([]);
        return;
      }

      if (res.ok) {
        const data = await res.json();
        const storyArray = Array.isArray(data) ? data : (data.stories || []);
        console.log('[ADMIN] Loaded stories:', storyArray.length);
        setStories(storyArray);
      } else {
        console.error('[ADMIN] Failed to load stories:', res.status);
        setStories([]);
      }
    } catch (err) {
      console.error('[ADMIN] Error loading stories:', err);
      setStories([]);
    } finally {
      setLoading(false);
    }
  }, [token, authFetch]);

  useEffect(() => {
    if (token && isAuthenticated) {
      fetchStories();
    }
  }, [fetchStories, token, isAuthenticated]);

  function handleLogout() {
    logout();
    navigate('/admin');
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        color: '#555',
        fontFamily: "'VT323', 'Courier New', monospace",
      }}>
        Redirecting to login...
      </div>
    );
  }

  const publishedCount = stories.filter(s => s.status === 'published').length;
  const draftCount = stories.length - publishedCount;

  const tabs = [
    { id: 'stories', label: 'ARCHIVES' },  
    { id: 'links', label: 'LINKS' },
    { id: 'games', label: 'GAMES' },
    { id: 'balance', label: '⚖ BALANCE' },      
    { id: 'subscribers', label: 'SUBSCRIBERS' },
    { id: 'devtools', label: '🔧 DEV TOOLS' },
  ];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '25px',
        paddingBottom: '15px',
        borderBottom: '1px solid #333',
      }}>
        <div>
          <h1 style={{ color: '#ff0040', margin: 0, fontSize: '1.3rem' }}>
            ▓ SYSOP TERMINAL ▓
          </h1>
          <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '5px' }}>
            System Operator Access — All changes are live
          </div>
        </div>
        <button
          className="bbs-button"
          onClick={handleLogout}
          style={{ borderColor: '#ff0040', color: '#ff0040', padding: '5px 15px', fontSize: '0.8rem' }}
        >
          LOGOUT
        </button>
      </div>

      {/* Auth Error Banner */}
      {authError && (
        <div style={{
          padding: '12px 15px',
          marginBottom: '20px',
          border: '1px solid #ff0040',
          color: '#ff0040',
          textAlign: 'center',
        }}>
          ⚠ SESSION EXPIRED — Please log out and log back in.
          <button
            className="bbs-button"
            onClick={handleLogout}
            style={{
              marginLeft: '15px',
              borderColor: '#ff0040',
              color: '#ff0040',
              padding: '3px 12px',
              fontSize: '0.8rem',
            }}
          >
            RE-LOGIN
          </button>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '25px',
        padding: '12px 15px',
        border: '1px solid #222',
      }}>
        <div>
          <span style={{ color: '#555', fontSize: '0.8rem' }}>TRANSCRIPTS: </span>
          <span style={{ color: '#00ff41' }}>{stories.length}</span>
        </div>
        <div>
          <span style={{ color: '#555', fontSize: '0.8rem' }}>PUBLISHED: </span>
          <span style={{ color: '#00ff41' }}>{publishedCount}</span>
        </div>
        <div>
          <span style={{ color: '#555', fontSize: '0.8rem' }}>DRAFTS: </span>
          <span style={{ color: '#ffb000' }}>{draftCount}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '20px',
        borderBottom: '1px solid #333',
        flexWrap: 'wrap',
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? '#111' : 'transparent',
              border: '1px solid #333',
              borderBottom: activeTab === tab.id ? '1px solid #111' : '1px solid #333',
              color: activeTab === tab.id
                ? (tab.id === 'devtools' ? '#ffb000' : '#00ff41')
                : '#555',
              padding: '10px 25px',
              cursor: 'pointer',
              fontFamily: "'VT323', 'Courier New', monospace",
              fontSize: '1rem',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'stories' && (
        loading ? (
          <div style={{ color: '#555', padding: '20px', textAlign: 'center' }}>
            Loading stories...
          </div>
        ) : (
          <StoryEditor
            token={token}
            authFetch={authFetch}
            stories={stories}
            onRefresh={fetchStories}
          />
        )
      )}
      {activeTab === 'links' && (
        <LinkEditor token={token} />
      )}
      {activeTab === 'games' && (
        <GamesEditor token={token} />
      )}
      {activeTab === 'balance' && (
        <BalanceAdmin token={token} />
      )}
      {activeTab === 'subscribers' && (
        <SubscribersPanel token={token} />
      )}
      {activeTab === 'devtools' && (
        <DevToolsPanel />
      )}
    </div>
  );
}